/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAllPublishedEvents } from '../../firebaseFunctions/mpsDashboard/mpsDashboard';
import { MapPin, ArrowRight, Calendar, CheckCircle, X } from 'lucide-react';
import placeholderImage from '../../assets/images/no_image_placeholder.jpg';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import DarkModeToggle from '../../components/ui/DarkModeToggle';
import { useTranslation } from 'react-i18next';

const Directory = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const fetchedEvents = await fetchAllPublishedEvents();
        setEvents(fetchedEvents);
      } catch (error) {
        console.error('Error loading events:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, []);

  const handleEventClick = (eventId) => {
    document.getElementById('auth-modal').showModal();
  };

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Floating Header with Language Switcher */}
      <div className="fixed top-0 right-0 z-50 flex items-center gap-2 p-3">
        <DarkModeToggle variant="icon" />
        <LanguageSwitcher />
      </div>

      {/* Hero Section */}
      <div className="bg-primary-400 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-center mb-4">
            {t('pages.directory.title')}
          </h1>
          <p className="text-xl text-center max-w-3xl mx-auto">
            {t('pages.directory.subtitle')}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <div className="mb-8">
          <button 
            onClick={() => window.location.href = '/'}
            className="flex items-center text-[#B22A2B] hover:text-[#8a2122] transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('pages.browseEvents.backToPebble')}
          </button>
        </div>

        {/* Call to Action for Non-Logged In Users */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-semibold text-grey-900 mb-2">
                {t('pages.directory.wantToList')}
              </h2>
              <p className="text-grey-600">
                {t('pages.directory.listDescription')}
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2 text-grey-700 bg-grey-100 rounded-lg hover:bg-grey-200 transition-colors"
              >
                {t('navigation.login')}
              </button>
              <button
                onClick={() => navigate('/create-account')}
                className="px-6 py-2 text-white bg-primary-400 rounded-lg hover:bg-primary-500 transition-colors flex items-center gap-2"
              >
                {t('navigation.signup')} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12 flex flex-col items-center transition-opacity duration-300">
            <p className="text-grey-600">{t('pages.directory.loadingEvents')}</p>
          </div>
        )}

        {/* Events Grid */}
        {!isLoading && events.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => {
              const isAcceptingVendors = event.applicationDeadline ? new Date(event.applicationDeadline) > new Date() : false;
              
              return (
                <div 
                  key={event.id} 
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 border border-grey-200"
                >
                  {/* Event Image with Badge */}
                  {event.eventCoverPhoto ? (
                    <div className="relative h-48 w-full">
                      <img 
                        src={event.eventCoverPhoto} 
                        alt={event.eventName}
                        className="w-full h-full object-cover"
                      />
                      {isAcceptingVendors && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-blue-600 text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-sm">
                            Accepting Vendors
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative h-48 w-full bg-grey-200 flex items-center justify-center">
                      <img src={placeholderImage} alt="Event" className="w-24 h-24 opacity-50" />
                      {isAcceptingVendors && (
                        <div className="absolute top-4 right-4">
                          <span className="bg-blue-600 text-white px-3 py-1.5 rounded-full text-sm font-medium shadow-sm">
                            Accepting Vendors
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="flex flex-col">
                      {/* Event Type Badge */}
                      {event.eventType && (
                        <span className="text-sm font-medium text-green-700 mb-2">
                          {event.eventType}
                        </span>
                      )}
                      
                      {/* Event Name */}
                      <h3 className="text-2xl font-bold text-grey-900 mb-3">
                        {event.eventName}
                      </h3>
                      
                      {/* Key Info Section */}
                      <div className="space-y-2 mb-4">
                        {/* Location */}
                        {event.location && (
                          <div className="flex items-center text-grey-600">
                            <MapPin size={20} className="mr-2 text-grey-400 flex-shrink-0" />
                            <span className="text-base">{event.location}</span>
                          </div>
                        )}
                        
                        {/* Date */}
                        <div className="flex items-center text-grey-600">
                          <Calendar size={20} className="mr-2 text-grey-400 flex-shrink-0" />
                          <span className="text-base">
                            {event.startDate ? (
                              <>
                                {new Date(event.startDate).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                                {event.endDate && event.endDate !== event.startDate && (
                                  <span className="text-sm text-grey-500">
                                    {' '}to {new Date(event.endDate).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </span>
                                )}
                              </>
                            ) : 'Date not set'}
                          </span>
                        </div>
                        
                        {/* Application Deadline */}
                        {isAcceptingVendors && event.applicationDeadline && (
                          <div className="flex items-center text-blue-600 font-medium">
                            <CheckCircle size={20} className="mr-2 flex-shrink-0" />
                            <span className="text-base">
                              Apply by {new Date(event.applicationDeadline).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Description */}
                      {event.eventDescription && (
                        <p className="text-grey-600 text-sm mb-4 line-clamp-2">
                          {event.eventDescription}
                        </p>
                      )}

                      {/* View Details Button */}
                      <button
                        onClick={() => handleEventClick(event.id)}
                        className="inline-flex items-center justify-center px-4 py-2 bg-[#B22A2B] text-white rounded-lg hover:bg-[#8a2122] transition-colors text-sm font-medium"
                      >
                        View Event Details
                        <ArrowRight size={16} className="ml-2" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && events.length === 0 && (
          <div className="text-center py-12">
            <p className="text-grey-600 text-lg">
              No events are currently published.
              <br />
              Check back soon for updates!
            </p>
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <dialog id="auth-modal" className="rounded-lg shadow-xl p-0 backdrop:bg-gray-800/50">
        <div className="w-full max-w-md bg-white rounded-lg overflow-hidden relative">
          <button
            onClick={() => document.getElementById('auth-modal').close()}
            className="absolute top-4 right-4 text-grey-400 hover:text-grey-600 transition-colors"
          >
            <X size={24} />
          </button>
          <div className="p-6">
            <h3 className="text-xl font-semibold text-grey-900 mb-4 pr-8">Sign in to view event details</h3>
            <p className="text-grey-600 mb-6">Create an account or sign in to view event details and apply to participate.</p>
            <div className="space-y-4">
              <button
                onClick={() => navigate('/login')}
                className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#B84141] hover:bg-[#923434] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B84141]"
              >
                Log In
              </button>
              <button
                onClick={() => navigate('/create-account')}
                className="w-full py-2 px-4 border border-grey-300 rounded-md shadow-sm text-sm font-medium text-grey-700 bg-white hover:bg-grey-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B84141]"
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default Directory;
